import { TextField } from "@mui/material";
import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
  FormFieldValidationDefinition,
} from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";

import { fetchBasicItemExistsByCustomId } from "../../helpers/fetch-basic-item-exists-by-custom-id";

export const CUSTOM_ID_TYPEAHEAD_FIELD_TYPE = "customIdTypeahead" as const;

export type CustomIdTypeaheadFieldDefinition = BaseFormFieldDefinition<
  typeof CUSTOM_ID_TYPEAHEAD_FIELD_TYPE
> & {
  placeholder?: string;
  /** Message shown when the entered id already exists. Defaults to a generic one. */
  duplicateMessage?: string;
  maxLength?: FormFieldValidationDefinition<number>;
};

// A plain react-hook-form async `validate` (rather than debounced local
// state) so it plugs into the existing required/error-message plumbing the
// same way every other field does: RHF only re-validates once the user
// pauses (mode "onBlur") or on every change (mode "all"), which is
// debouncing enough for a duplicate-id lookup.
export function CustomIdTypeaheadFormField({
  disabled,
  duplicateMessage,
  errorMessage,
  maxLength,
  name,
  placeholder,
  register,
  required,
  sx,
}: CustomIdTypeaheadFieldDefinition & AdditionalFormFieldProps) {
  return (
    <TextField
      {...register(name, {
        disabled,
        maxLength,
        required,
        validate: async (value: unknown) => {
          const trimmed =
            typeof value === "string" ? value.trim() : String(value ?? "");
          if (!trimmed) return true;
          const exists = await fetchBasicItemExistsByCustomId(trimmed);
          return exists
            ? (duplicateMessage ?? "This identifier already exists")
            : true;
        },
      })}
      error={!!errorMessage}
      fullWidth
      helperText={errorMessage}
      placeholder={placeholder}
      sx={sx}
      slotProps={{
        input: {
          style: { borderRadius: "6px", height: "45px" },
        },
      }}
    />
  );
}
